import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Paragraph, Spinner, tokens } from '@treams/treams-ui';

import { cellColors } from '../talentReviewColors';

const CELL_SIZE = 160;
const LABEL_COL = 32;

const Layout = styled(Box)`
	width: fit-content;
	max-width: 100%;
	overflow-x: auto;
	padding: 16px;
	background: ${(props) => props.theme.colors.white};
	border-radius: 8px;
`;

const Grid = styled.div<{ $columns: number }>`
	display: grid;
	grid-template-columns: ${LABEL_COL}px repeat(${(props) => props.$columns}, ${CELL_SIZE}px);
	grid-auto-rows: ${CELL_SIZE}px;
	gap: 8px;
	align-items: stretch;
`;

const AxisRow = styled.div<{ $columns: number; $tight?: boolean }>`
	display: grid;
	grid-template-columns: ${LABEL_COL}px repeat(${(props) => props.$columns}, ${CELL_SIZE}px);
	gap: 8px;
	margin-top: ${(props) => (props.$tight ? 4 : 10)}px;
`;

const YLabel = styled(Flex)`
	flex-grow: 0;
	flex-shrink: 0;
	width: ${LABEL_COL}px;
	align-items: center;
	justify-content: center;
`;

const YLabelText = styled(Paragraph)<{ $main?: boolean }>`
	margin: 0;
	writing-mode: vertical-rl;
	transform: rotate(180deg);
	font-size: 13px;
	text-align: center;
	color: ${(props) => (props.$main ? tokens.text.secondary : props.theme.colors.lightBlueDark)};
	font-weight: ${(props) => (props.$main ? 'bold' : 'normal')};
`;

const XLabel = styled(Paragraph)<{ $main?: boolean }>`
	margin: 0;
	min-width: 0;
	text-align: center;
	font-size: 13px;
	color: ${(props) => (props.$main ? tokens.text.secondary : props.theme.colors.lightBlueDark)};
	font-weight: ${(props) => (props.$main ? 'bold' : 'normal')};
`;

const XAxisTitle = styled(XLabel)`
	grid-column: 2 / -1;
`;

const Cell = styled(Flex)<{ $hex: string; $labelHex: string }>`
	box-sizing: border-box;
	width: ${CELL_SIZE}px;
	height: ${CELL_SIZE}px;
	padding: 10px;
	align-items: center;
	justify-content: center;
	text-align: center;
	border-radius: 8px;
	border: 2px solid ${(props) => props.$hex};
	background: ${(props) => props.theme.colors.white};
	color: ${(props) => props.$labelHex};
	font-size: 14px;
	line-height: 1.3;
	overflow: hidden;
`;

export default function TalentReviewTemplatePreview(props: { templateID: string }) {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const query = graphql.useTalentReviewTemplateQuery({
		variables: { id: props.templateID },
	});

	const template = query.data?.talentReviewTemplate as graphql.TalentReviewTemplate | undefined;

	if (query.loading && !template) {
		return (
			<Box mt={3}>
				<Spinner />
			</Box>
		);
	}

	if (!template?.cells?.length || !template.xAxis.ticks.length || !template.yAxis.ticks.length) {
		return null;
	}

	const label = (value?: graphql.LocalizedString) => localize(value)?.text ?? '';
	const columns = template.xAxis.ticks.length;
	const rows = [...template.yAxis.ticks.keys()].reverse();

	return (
		<Box role="img" aria-label={t('common.settings.momentTemplates.edit.talentReviewGridPreview')}>
			<Layout>
				<Flex>
					<YLabel mr={1}>
						<YLabelText $main>{label(template.yAxis.label)}</YLabelText>
					</YLabel>
					<Box>
						<Grid $columns={columns}>
							{rows.map((row) => (
								<Fragment key={row}>
									<YLabel>
										<YLabelText>{label(template.yAxis.ticks[row])}</YLabelText>
									</YLabel>
									{template.xAxis.ticks.map((_, column) => {
										const cell = template.cells[row]?.[column];
										const colors = cellColors(row, column, rows.length, columns);

										return (
											<Cell key={`${row}-${column}`} $hex={colors.border} $labelHex={colors.label}>
												{label(cell?.label)}
											</Cell>
										);
									})}
								</Fragment>
							))}
						</Grid>
						<AxisRow $columns={columns}>
							<YLabel />
							{template.xAxis.ticks.map((tick, column) => (
								<XLabel key={column}>{label(tick)}</XLabel>
							))}
						</AxisRow>
						<AxisRow $columns={columns} $tight>
							<YLabel />
							<XAxisTitle $main>{label(template.xAxis.label)}</XAxisTitle>
						</AxisRow>
					</Box>
				</Flex>
			</Layout>
		</Box>
	);
}
